import { useCallback, useEffect, useRef } from "react";

import productComponentImpressionEvent from "../../../../../services/dataLayer/productComponentImpressionEvent";

const MIN_VISIBLE_DURATION_MS = 500;
const ROW_VISIBILITY_THRESHOLD = 1;

const getProductsPerRow = (isLargeViewport, isMediumViewport) =>
  isLargeViewport ? 4 : isMediumViewport ? 3 : 2;

const findRowIndexForTargetInMap = (rowItemElements, target) => {
  for (const [rowIndex, itemElement] of rowItemElements.entries()) {
    if (itemElement === target) {
      return rowIndex;
    }
  }

  return null;
};

const getRowStartIndex = (rowIndex, productsPerRow, leadingGridItemCount) =>
  Math.max(0, rowIndex * productsPerRow - leadingGridItemCount);

const getItemsForRow = (
  items,
  rowIndex,
  productsPerRow,
  leadingGridItemCount
) => {
  const rowStartIndex = getRowStartIndex(
    rowIndex,
    productsPerRow,
    leadingGridItemCount
  );
  const nextRowStartIndex = getRowStartIndex(
    rowIndex + 1,
    productsPerRow,
    leadingGridItemCount
  );

  return items.slice(rowStartIndex, nextRowStartIndex);
};

const getRowItemIds = rowItems => rowItems.map(item => item.id).join(",");

const resetFiredRowsWithChangedItems = ({
  firedRows,
  firedRowItemIds,
  items,
  productsPerRow,
  leadingGridItemCount
}) => {
  firedRowItemIds.forEach((itemIds, rowIndex) => {
    const rowItems = getItemsForRow(
      items,
      rowIndex,
      productsPerRow,
      leadingGridItemCount
    );

    if (getRowItemIds(rowItems) !== itemIds) {
      firedRows.delete(rowIndex);
      firedRowItemIds.delete(rowIndex);
    }
  });
};

const tryFireProductComponentImpression = (
  rowIndex,
  rowItems,
  productsPerRow,
  leadingGridItemCount
) => {
  if (rowItems.length === 0) {
    return true;
  }

  try {
    productComponentImpressionEvent(
      rowIndex,
      rowItems,
      productsPerRow,
      ...(leadingGridItemCount ? [leadingGridItemCount] : [])
    );

    return true;
  } catch {
    return false;
  }
};

const useProductRowsInView = ({
  isLargeViewport,
  isMediumViewport,
  items,
  leadingGridItemCount = 0
}) => {
  const productsPerRow = getProductsPerRow(isLargeViewport, isMediumViewport);

  const rowItemElementsRef = useRef(new Map());
  const rowRefCallbacksRef = useRef(new Map());
  const firedRowsRef = useRef(new Set());
  const firedRowItemIdsRef = useRef(new Map());
  const previousItemsRef = useRef(items);
  const rowTimersRef = useRef(new Map());
  const observerRef = useRef(null);

  const clearPendingRowTimer = useCallback(rowIndex => {
    const pendingTimer = rowTimersRef.current.get(rowIndex);

    if (!pendingTimer) {
      return;
    }

    clearTimeout(pendingTimer);
    rowTimersRef.current.delete(rowIndex);
  }, []);

  const clearAllPendingRowTimers = useCallback(() => {
    rowTimersRef.current.forEach(timer => clearTimeout(timer));
    rowTimersRef.current.clear();
  }, []);

  const fireRowVisibleAction = useCallback(
    (rowIndex, target) => {
      if (firedRowsRef.current.has(rowIndex)) {
        return;
      }
      rowTimersRef.current.delete(rowIndex);

      const rowItems = getItemsForRow(
        items,
        rowIndex,
        productsPerRow,
        leadingGridItemCount
      );

      const hasFiredSuccessfully = tryFireProductComponentImpression(
        rowIndex,
        rowItems,
        productsPerRow,
        leadingGridItemCount
      );

      if (!hasFiredSuccessfully) {
        return;
      }

      firedRowsRef.current.add(rowIndex);
      firedRowItemIdsRef.current.set(rowIndex, getRowItemIds(rowItems));

      if (observerRef.current) {
        observerRef.current.unobserve(target);
      }
    },
    [items, leadingGridItemCount, productsPerRow]
  );

  const handleIntersect = useCallback(
    entries => {
      entries.forEach(entry => {
        const rowIndex = findRowIndexForTargetInMap(
          rowItemElementsRef.current,
          entry.target
        );

        if (rowIndex === null) {
          return;
        }

        if (!entry.isIntersecting) {
          clearPendingRowTimer(rowIndex);

          return;
        }

        if (
          firedRowsRef.current.has(rowIndex) ||
          rowTimersRef.current.has(rowIndex)
        ) {
          return;
        }

        const timer = setTimeout(
          () => fireRowVisibleAction(rowIndex, entry.target),
          MIN_VISIBLE_DURATION_MS
        );

        rowTimersRef.current.set(rowIndex, timer);
      });
    },
    [clearPendingRowTimer, fireRowVisibleAction]
  );

  const syncRowObserver = useCallback(
    (rowIndex, itemElement) => {
      const existingRowElement = rowItemElementsRef.current.get(rowIndex);

      if (existingRowElement && observerRef.current) {
        observerRef.current.unobserve(existingRowElement);
      }

      if (itemElement) {
        rowItemElementsRef.current.set(rowIndex, itemElement);
        if (observerRef.current) {
          observerRef.current.observe(itemElement);
        }

        return;
      }

      clearPendingRowTimer(rowIndex);
      rowItemElementsRef.current.delete(rowIndex);
    },
    [clearPendingRowTimer]
  );

  const getRefForFirstItemInRow = useCallback(
    itemIndex => {
      const visualItemIndex = itemIndex + leadingGridItemCount;
      const rowIndex = Math.floor(visualItemIndex / productsPerRow);
      const isFirstInRow =
        itemIndex ===
        getRowStartIndex(rowIndex, productsPerRow, leadingGridItemCount);

      if (!isFirstInRow) {
        return null;
      }

      if (!rowRefCallbacksRef.current.has(rowIndex)) {
        rowRefCallbacksRef.current.set(rowIndex, itemElement =>
          syncRowObserver(rowIndex, itemElement)
        );
      }

      return rowRefCallbacksRef.current.get(rowIndex);
    },
    [leadingGridItemCount, productsPerRow, syncRowObserver]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      return;
    }

    clearAllPendingRowTimers();

    if (previousItemsRef.current !== items) {
      resetFiredRowsWithChangedItems({
        firedRows: firedRowsRef.current,
        firedRowItemIds: firedRowItemIdsRef.current,
        items,
        productsPerRow,
        leadingGridItemCount
      });

      previousItemsRef.current = items;
    }

    observerRef.current?.disconnect();

    observerRef.current = new window.IntersectionObserver(handleIntersect, {
      threshold: ROW_VISIBILITY_THRESHOLD
    });

    rowItemElementsRef.current.forEach(itemElement => {
      observerRef.current.observe(itemElement);
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      clearAllPendingRowTimers();
    };
  }, [
    clearAllPendingRowTimers,
    handleIntersect,
    items,
    leadingGridItemCount,
    productsPerRow
  ]);

  return {
    getRefForFirstItemInRow
  };
};

export default useProductRowsInView;
